import { Temporal } from "temporal-polyfill";

/**
 * Calculates the previous date range with the same duration as the current date range
 * @param dateRange Current date range as a tuple of [startDate, endDate]
 * @returns Previous date range with the same duration as a tuple of [previousStartDate, previousEndDate]
 *
 * @example
 * // If dateRange is [2025-04-01, 2025-05-01] (30 days)
 * // Returns [2025-03-01, 2025-04-01] (30 days before)
 */
export const getPreviousDateRange = (
  dateRange: [Temporal.PlainDate, Temporal.PlainDate],
): [Temporal.PlainDate, Temporal.PlainDate] => {
  const [start, end] = dateRange;
  const daysDifference = end.since(start).days;
  const previousEnd = start.subtract({ days: 1 });
  const previousStart = previousEnd.subtract({ days: daysDifference });

  return [previousStart, previousEnd];
};

/**
 * Calculates the previous month range with the same duration as the current month range
 * @param dateRange Current month range as a tuple of [startDate, endDate]
 * @returns Previous month range with the same duration as a tuple of [previousStartDate, previousEndDate]
 *
 * @example
 * If dateRange is [2025-04-01, 2025-05-01] (1 month)
 * Returns [2025-03-01, 2025-04-01] (1 month before)
 */
export const getPreviousMonthRange = (
  dateRange: [Temporal.PlainDate, Temporal.PlainDate],
): [Temporal.PlainDate, Temporal.PlainDate] => {
  const [start, end] = dateRange;
  const monthDifference = start.until(end, { largestUnit: "month" }).months;
  const previousEnd = start.subtract({ months: 1 });
  const previousStart = previousEnd.subtract({ months: monthDifference });

  return [previousStart, previousEnd];
};

/**
 * Formats a date range as a human-readable string
 * @param dateRange Date range as a tuple of [startDate, endDate]
 * @returns Formatted date range string (e.g., "Apr 1 - May 1" or "Apr 1 - 30")
 */
export const formatDateRange = (dateRange: [Temporal.PlainDate, Temporal.PlainDate]): string => {
  const [start, end] = dateRange;

  return `${start.toLocaleString("en-US", { month: "short" })} ${start.day} - ${
    start.month === end.month ? end.day : `${end.toLocaleString("en-US", { month: "short" })} ${end.day}`
  }`;
};

/* Usage Examples:

// Example 1: 30-day range
const currentRange30Days = [
  Temporal.PlainDate.from('2025-04-01'),
  Temporal.PlainDate.from('2025-05-01')
] as [Temporal.PlainDate, Temporal.PlainDate];

const prevRange30Days = getPreviousDateRange(currentRange30Days);
// Result: [2025-03-02, 2025-04-01]

// Example 2: 7-day range
const currentRange7Days = [
  Temporal.PlainDate.from('2025-04-01'),
  Temporal.PlainDate.from('2025-04-08')
] as [Temporal.PlainDate, Temporal.PlainDate];

const prevRange7Days = getPreviousDateRange(currentRange7Days);
// Result: [2025-03-25, 2025-04-01]

// Example 3: 2-day range
const currentRange2Days = [
  Temporal.PlainDate.from('2025-04-01'),
  Temporal.PlainDate.from('2025-04-03')
] as [Temporal.PlainDate, Temporal.PlainDate];

const prevRange2Days = getPreviousDateRange(currentRange2Days);
// Result: [2025-03-30, 2025-04-01]
*/
export function formatFullDateTimeRange(range: [Temporal.PlainDateTime, Temporal.PlainDateTime]) {
  const [start, end] = range;

  // Set start to 00:00 and end to 23:59
  const startWithTime = start.with({ hour: 0, minute: 0 });
  const endWithTime = end.with({ hour: 23, minute: 59 });

  return `${startWithTime
    .toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(/at/, "")} - ${endWithTime
    .toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(/at/, "")}`;
}

export function refineTimezone(localeString: string) {
  return localeString
    .replace(/at/, "")
    .replace(/Korean Standard Time/, "(KST)")
    .replace(/Pacific Daylight Time/, "(PDT)");
}

/**
 * Calculates and formats the remaining days until a given end date
 * @param endDate The end date string in ISO format (YYYY-MM-DD)
 * @returns Formatted string showing the remaining days (e.g., "5 days remaining" or "Ended")
 */
export function calculateRemainingDays(endDate?: string): string {
  if (!endDate) return "Date not specified";

  try {
    const today = Temporal.Now.plainDateISO();
    const targetDate = Temporal.PlainDate.from(endDate);

    const daysRemaining = targetDate.since(today, { largestUnit: "days" }).days;

    if (daysRemaining < 0) {
      return "Ended";
    }

    if (daysRemaining === 0) {
      return "Last day to apply";
    }

    if (daysRemaining === 1) {
      return "1 day to apply";
    }

    return `${daysRemaining} days to apply`;
  } catch (error) {
    console.error("Error calculating remaining days:", error);
    return "Date calculation error";
  }
}
